import { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useBrand, DEFAULT_THEME, applyTheme } from "../lib/brand";
import { PageHeader } from "../components/ui-bits";
import { Palette, RotateCcw, Save, Eye, Building2, Upload, Trash2, Copy as CopyIcon, Image as ImageIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ShopifyConnectCard } from "../components/ShopifyConnectCard";
import { SamplingSettingsCard } from "../components/sampling/SamplingDashboard";

const FIELDS = [
  { key: "welcome_message", label: "Welcome message", type: "text" },
  { key: "mode", label: "Mode", type: "select", options: ["dark","light"] },
  { key: "radius", label: "Rounded corners", type: "select", options: ["none","sm","md","lg"] },
  { key: "density", label: "Card density", type: "select", options: ["compact","standard","spacious"] },
  { key: "show_background_image", label: "Show background image", type: "boolean" },
];
const COLOR_FIELDS = [
  ["background_color","Background"],
  ["surface_color","Surface"],
  ["sidebar_color","Sidebar"],
  ["card_background_color","Card background"],
  ["button_color","Button"],
  ["button_hover_color","Button hover"],
  ["accent_color","Accent"],
  ["text_color","Text"],
  ["text_muted_color","Text muted"],
  ["border_color","Border"],
];
const SIZE_FIELDS = [
  ["title_size","Main title"],
  ["section_title_size","Section title"],
  ["body_size","Body text"],
  ["button_size","Button text"],
  ["menu_size","Menu text"],
];

export default function BrandSettingsPage() {
  const { activeBrand, refresh, updateActiveTheme } = useBrand();
  const [tab, setTab] = useState("identity");
  const [brand, setBrand] = useState(null);
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    if (activeBrand) {
      setBrand({ ...activeBrand });
      setTheme({ ...DEFAULT_THEME, ...(activeBrand.theme || {}) });
    }
  }, [activeBrand]);

  if (!brand || !theme) return <div className="text-[#737373]">Loading…</div>;

  const preview = () => applyTheme(theme);
  const reset = async () => {
    if (!window.confirm("Reset theme to default?")) return;
    const { data } = await api.post(`/brands/${brand.id}/theme/reset`);
    setTheme({ ...DEFAULT_THEME });
    updateActiveTheme(data.theme);
    setOk("Theme reset to default");
    setTimeout(()=>setOk(""), 2000);
  };
  const saveTheme = async () => {
    setSaving(true); setError(""); setOk("");
    try {
      const { data } = await api.put(`/brands/${brand.id}/theme`, theme);
      updateActiveTheme(data.theme);
      setOk("Theme saved");
      await refresh();
      setTimeout(()=>setOk(""), 2000);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };
  const saveIdentity = async () => {
    setSaving(true); setError(""); setOk("");
    try {
      const payload = {
        name: brand.name, description: brand.description || "",
        website: brand.website || "", instagram: brand.instagram || "",
        contact_email: brand.contact_email || "",
      };
      await api.put(`/brands/${brand.id}`, payload);
      setOk("Saved");
      await refresh();
      setTimeout(()=>setOk(""), 2000);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div data-testid="brand-settings-page">
      <PageHeader eyebrow="Brand workspace" title="Brand Settings" description={`Customize the identity and visual theme of ${brand.name}. Settings apply only to this brand.`}/>

      <div className="flex gap-1 border-b mb-6" style={{ borderColor: "var(--border)" }}>
        <button data-testid="settings-tab-identity" onClick={()=>setTab("identity")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="identity"?"border-current":"border-transparent"}`} style={{ color: tab==="identity" ? "var(--text-primary)" : "var(--text-secondary)" }}>Identity</button>
        <button data-testid="settings-tab-theme" onClick={()=>setTab("theme")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="theme"?"border-current":"border-transparent"}`} style={{ color: tab==="theme" ? "var(--text-primary)" : "var(--text-secondary)" }}>Aspetto</button>
        <button data-testid="settings-tab-brand-images" onClick={()=>setTab("images")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="images"?"border-current":"border-transparent"}`} style={{ color: tab==="images" ? "var(--text-primary)" : "var(--text-secondary)" }}>Logo & Background</button>
        <button data-testid="settings-tab-sampling" onClick={()=>setTab("sampling")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="sampling"?"border-current":"border-transparent"}`} style={{ color: tab==="sampling" ? "var(--text-primary)" : "var(--text-secondary)" }}>Campionario</button>
        <button data-testid="settings-tab-integrations" onClick={()=>setTab("integrations")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="integrations"?"border-current":"border-transparent"}`} style={{ color: tab==="integrations" ? "var(--text-primary)" : "var(--text-secondary)" }}>Integrazioni</button>
        <button data-testid="settings-tab-danger" onClick={()=>setTab("danger")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="danger"?"border-current":"border-transparent"}`} style={{ color: tab==="danger" ? "var(--text-primary)" : "var(--text-secondary)" }}>Actions</button>
      </div>

      {tab === "identity" && (
        <div className="card-dark p-6 space-y-4 max-w-2xl">
          <div className="flex items-center gap-3 mb-2">
            <Building2 size={16} className="text-[#0A0A0A]"/>
            <div className="label-caps">Brand identity</div>
          </div>
          <div><label className="label-caps block mb-2">Brand name</label>
            <input data-testid="brand-name" className="input-dark" value={brand.name||""} onChange={(e)=>setBrand({...brand, name: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Description</label>
            <textarea rows={3} className="input-dark" value={brand.description||""} onChange={(e)=>setBrand({...brand, description: e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label-caps block mb-2">Website</label>
              <input className="input-dark" value={brand.website||""} onChange={(e)=>setBrand({...brand, website: e.target.value})}/></div>
            <div><label className="label-caps block mb-2">Instagram</label>
              <input className="input-dark" value={brand.instagram||""} onChange={(e)=>setBrand({...brand, instagram: e.target.value})}/></div>
            <div className="col-span-2"><label className="label-caps block mb-2">Contact email</label>
              <input type="email" className="input-dark" value={brand.contact_email||""} onChange={(e)=>setBrand({...brand, contact_email: e.target.value})}/></div>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          {ok && <div className="text-green-400 text-sm">{ok}</div>}
          <button data-testid="save-identity" onClick={saveIdentity} disabled={saving} className="btn-gold inline-flex items-center gap-2"><Save size={14}/> {saving?"Saving…":"Save identity"}</button>
        </div>
      )}

      {tab === "theme" && (
        <AppearanceTab brand={brand} theme={theme} setTheme={setTheme}
                       updateActiveTheme={updateActiveTheme} refresh={refresh}
                       saving={saving} setSaving={setSaving}
                       error={error} setError={setError} ok={ok} setOk={setOk} />
      )}
      {tab === "images" && <ImagesTab brand={brand} setBrand={setBrand}/>}
      {tab === "integrations" && <ShopifyConnectCard/>}
      {tab === "sampling" && <SamplingSettingsCard/>}
      {tab === "danger" && <ActionsTab brand={brand}/>}
    </div>
  );
}

function ImagesTab({ brand, setBrand }) {
  const { refresh } = useBrand();
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [uploadingKind, setUploadingKind] = useState(null); // "logo" | "background"

  const validate = (file) => {
    const okTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!okTypes.includes((file.type || "").toLowerCase())) {
      return "Formato non supportato. Carica PNG, JPG o WEBP.";
    }
    if (file.size > 8 * 1024 * 1024) return "Immagine troppo grande. Massimo 8 MB.";
    return null;
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const err = validate(file);
    if (err) { setError(err); return; }
    setUploadingKind(kind); setError(""); setOk("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("linked_type", kind === "logo" ? "brand_logo" : "brand_background");
      fd.append("linked_id", brand.id);
      const { data: fileDoc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      // The upload endpoint already patches brand.logo_file_id / background_file_id.
      // Re-fetch the brand to get the persisted value.
      const { data: fresh } = await api.get(`/brands/${brand.id}`);
      setBrand(fresh);
      await refresh();
      setOk(kind === "logo" ? "Logo caricato" : "Background caricato");
      setTimeout(()=>setOk(""), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setUploadingKind(null);
  };

  const remove = async (kind) => {
    setUploadingKind(kind); setError(""); setOk("");
    try {
      const patch = kind === "logo" ? { logo_file_id: null } : { background_file_id: null };
      const { data } = await api.put(`/brands/${brand.id}`, { name: brand.name, ...patch });
      setBrand(data);
      await refresh();
      setOk(kind === "logo" ? "Logo rimosso" : "Background rimosso");
      setTimeout(()=>setOk(""), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setUploadingKind(null);
  };

  const logoUrl = fileUrl(brand.logo_file_id);
  const bgUrl = fileUrl(brand.background_file_id);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl" data-testid="brand-images-tab">
      <div className="card-dark p-6">
        <div className="label-caps mb-3 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
          <ImageIcon size={14}/> Logo del brand
        </div>
        <div className="text-[11px] mb-3" style={{ color: "var(--text-secondary)" }}>
          Consigliato: PNG con sfondo trasparente · min 512×512 · max 8 MB.
        </div>
        <div
          className="aspect-square mb-4 flex items-center justify-center overflow-hidden"
          style={{ background: "var(--surface-tertiary)", border: "1px solid var(--border)" }}
          data-testid="brand-logo-preview"
        >
          {logoUrl
            ? <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain"/>
            : <div className="label-caps" style={{ color: "var(--text-muted)" }}>Nessun logo</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="btn-gold inline-flex items-center gap-2 cursor-pointer" data-testid="brand-logo-upload">
            <Upload size={14}/>
            {uploadingKind === "logo" ? "Caricamento…" : (logoUrl ? "Sostituisci" : "Carica logo")}
            <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" hidden onChange={(e)=>upload("logo", e.target.files?.[0])}/>
          </label>
          {logoUrl && (
            <button
              type="button"
              disabled={uploadingKind === "logo"}
              onClick={()=>remove("logo")}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="brand-logo-remove"
            >
              <Trash2 size={14}/> Rimuovi
            </button>
          )}
        </div>
      </div>

      <div className="card-dark p-6">
        <div className="label-caps mb-3 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
          <ImageIcon size={14}/> Background del brand
        </div>
        <div className="text-[11px] mb-3" style={{ color: "var(--text-secondary)" }}>
          Utilizzato come copertina della dashboard del brand · PNG/JPG/WEBP · max 8 MB. Un overlay automatico garantisce la leggibilità.
        </div>
        <div
          className="aspect-video mb-4 flex items-center justify-center overflow-hidden relative"
          style={{ background: "var(--surface-tertiary)", border: "1px solid var(--border)" }}
          data-testid="brand-bg-preview"
        >
          {bgUrl
            ? <img src={bgUrl} alt="background" className="w-full h-full object-cover"/>
            : <div className="label-caps" style={{ color: "var(--text-muted)" }}>Nessun background</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="btn-gold inline-flex items-center gap-2 cursor-pointer" data-testid="brand-bg-upload">
            <Upload size={14}/>
            {uploadingKind === "background" ? "Caricamento…" : (bgUrl ? "Sostituisci" : "Carica background")}
            <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" hidden onChange={(e)=>upload("background", e.target.files?.[0])}/>
          </label>
          {bgUrl && (
            <button
              type="button"
              disabled={uploadingKind === "background"}
              onClick={()=>remove("background")}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="brand-bg-remove"
            >
              <Trash2 size={14}/> Rimuovi
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-sm md:col-span-2" style={{ color: "var(--error)" }} data-testid="brand-images-error">{error}</div>}
      {ok && <div className="text-sm md:col-span-2" style={{ color: "var(--success)" }} data-testid="brand-images-ok">{ok}</div>}
    </div>
  );
}

function ActionsTab({ brand }) {
  const nav = useNavigate();
  const [dupName, setDupName] = useState(`${brand.name} Copy`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const duplicate = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/brands/${brand.id}/duplicate`, { new_name: dupName });
      window.alert(`Duplicated as "${data.name}". Return to Brand Accounts to enter it.`);
      nav("/select-brand");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="card-dark p-6 max-w-2xl space-y-4">
      <div className="label-caps flex items-center gap-2"><CopyIcon size={14} className="text-[#0A0A0A]"/> Duplicate brand (structure only)</div>
      <p className="text-xs text-[#525252]">Creates a new brand with the same theme, identity settings, and description — but with an EMPTY workspace. No collections, garments, files, orders, or tasks are copied.</p>
      <div className="flex gap-3">
        <input data-testid="duplicate-name" className="input-dark flex-1" value={dupName} onChange={(e)=>setDupName(e.target.value)} placeholder="New brand name"/>
        <button data-testid="duplicate-btn" onClick={duplicate} disabled={busy || !dupName.trim()} className="btn-gold inline-flex items-center gap-2 disabled:opacity-50">
          <CopyIcon size={14}/> {busy ? "Duplicating…" : "Duplicate"}
        </button>
      </div>
      {error && <div className="text-red-600 text-sm">{error}</div>}
    </div>
  );
}


function AppearanceTab({ brand, theme, setTheme, updateActiveTheme, refresh, saving, setSaving, error, setError, ok, setOk }) {
  const currentMode = theme.theme_mode || theme.mode || "light";
  const save = async (mode) => {
    setSaving(true); setError(""); setOk("");
    try {
      // Optimistically apply so the client sees the switch instantly.
      applyTheme({ theme_mode: mode });
      const patch = { theme_mode: mode, mode };
      const { data } = await api.put(`/brands/${brand.id}/theme`, patch);
      setTheme({ ...theme, ...patch, ...(data?.theme || {}) });
      updateActiveTheme(data.theme);
      setOk("Aspetto salvato per questo brand");
      await refresh();
      setTimeout(()=>setOk(""), 2200);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };
  const preview = (mode) => applyTheme({ theme_mode: mode });

  const options = [
    {
      key: "light", label: "Tema chiaro",
      description: "Sfondo bianco, testo nero, ideale per la lettura diurna e per allineare i colori del capo alle proporzioni tipografiche.",
      bg: "#FFFFFF", surface: "#F5F5F5", text: "#0A0A0A", muted: "#525252", border: "#E5E5E5",
      logo: "/branding/logo-mms-black.svg",
    },
    {
      key: "dark", label: "Tema scuro",
      description: "Sfondo nero, testo bianco, per lunghe sessioni notturne di lavoro sul cartamodello. Il rosso MMS viene usato come accento puntuale.",
      bg: "#0A0A0A", surface: "#141414", text: "#F5F5F5", muted: "#B3B3B3", border: "#262626",
      logo: "/branding/logo-mms-white.svg",
    },
  ];

  return (
    <div className="max-w-4xl" data-testid="appearance-tab">
      <div className="card-dark p-6 mb-4">
        <div className="flex items-center gap-3 mb-2">
          <Palette size={16} style={{ color: "var(--text-primary)" }}/>
          <div className="label-caps">Aspetto</div>
        </div>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Scegli come vuoi vedere <strong>{brand.name}</strong> su MMS HUB CENTER. La scelta viene salvata a livello di brand e applicata a tutti i membri del team, su qualsiasi dispositivo. Il cambio è immediato.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {options.map((opt) => {
          const active = currentMode === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              disabled={saving}
              onClick={() => save(opt.key)}
              onMouseEnter={() => preview(opt.key)}
              onMouseLeave={() => preview(currentMode)}
              data-testid={`appearance-${opt.key}`}
              className="text-left transition-all"
              style={{
                background: opt.surface,
                border: `1px solid ${active ? "var(--brand-accent)" : opt.border}`,
                borderRadius: "var(--radius)",
                overflow: "hidden",
                boxShadow: active ? "0 0 0 3px var(--brand-accent-soft)" : "none",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              <div style={{ background: opt.bg, padding: "24px", borderBottom: `1px solid ${opt.border}` }}>
                <div className="flex items-center justify-between mb-4">
                  <img src={opt.logo} alt="MMS" style={{ height: 28 }} />
                  {active && (
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5"
                      style={{ background: "var(--brand-accent)", color: "#FFFFFF", borderRadius: "2px" }}
                      data-testid={`appearance-${opt.key}-active`}
                    >
                      Attivo
                    </span>
                  )}
                </div>
                <div className="font-serif text-xl leading-tight" style={{ color: opt.text }}>{opt.label}</div>
                <div className="text-[10px] uppercase tracking-widest mt-2" style={{ color: opt.muted }}>ABCD 1234 · Anteprima</div>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-widest font-medium"
                     style={{ background: opt.key === "light" ? "#0A0A0A" : "#FFFFFF", color: opt.key === "light" ? "#FFFFFF" : "#0A0A0A" }}>
                  Pulsante primario
                </div>
              </div>
              <div style={{ padding: "16px 20px" }}>
                <div className="text-sm" style={{ color: opt.text }}>{opt.label}</div>
                <div className="text-xs mt-1" style={{ color: opt.muted }}>{opt.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {error && <div className="text-sm mt-4" style={{ color: "var(--error)" }}>{error}</div>}
      {ok && <div className="text-sm mt-4" style={{ color: "var(--success)" }} data-testid="appearance-ok">{ok}</div>}
    </div>
  );
}
